import { useLayoutEffect, useState, useCallback, ReactNode } from 'react';

import { useEventListener } from '@sureapp/platform-sdk';
import { Button, TButton } from '@sureapp/wl-toggle-common';
import clsx from 'clsx';
import ReactModal from 'react-modal';

import HorizontalRule from '@@/auto/components/HorizontalRule';
import Text from '@@/auto/components/Text';

import classes from './Modal.module.css';

if (process.env.NODE_ENV !== 'test') {
  ReactModal.setAppElement('body');
}

export type TModal = {
  'data-testid'?: string;
  children?: ReactNode;
  className?: string;
  hasFooter?: boolean;
  hideCloseButton?: boolean;
  isOpen: boolean;
  onAfterOpen?: () => void;
  onRequestClose: () => void;
  overrideClasses?: {
    root?: string;
    footer?: string;
    body?: string;
    title?: string;
  };
  primaryFooterButton?: TButton;
  secondaryFooterButton?: TButton;
  shouldCloseOnOverlayClick?: boolean;
  title?: ReactNode;
};

function Modal({
  'data-testid': dataTestId = 'modal',
  children,
  className,
  hasFooter = false,
  hideCloseButton = false,
  isOpen,
  onAfterOpen,
  onRequestClose,
  overrideClasses,
  primaryFooterButton,
  secondaryFooterButton,
  shouldCloseOnOverlayClick,
  title = '',
}: TModal) {
  const [modalElement, setModalElement] = useState<null | HTMLElement>(null);
  const [
    isModalSmallerThanWindowHeight,
    setIsModalSmallerThanWindowHeight,
  ] = useState(true);

  const verticallyAlignModal = useCallback(() => {
    if (!isOpen || !modalElement) {
      return;
    }

    setIsModalSmallerThanWindowHeight(
      modalElement.getBoundingClientRect().height <= window.innerHeight
    );
  }, [isOpen, modalElement]);

  useLayoutEffect(verticallyAlignModal, [verticallyAlignModal]);
  useEventListener('resize', verticallyAlignModal);

  return (
    <ReactModal
      className={clsx(
        classes.modal,
        {
          [classes.modal_with_close]: !hideCloseButton,
        },
        className,
        overrideClasses?.root
      )}
      overlayClassName={clsx(classes.overlay, {
        [classes.overlay_centered]: isModalSmallerThanWindowHeight,
      })}
      contentRef={(element) => setModalElement(element)}
      isOpen={isOpen}
      onAfterOpen={onAfterOpen}
      onRequestClose={onRequestClose}
      contentLabel={`${title} modal`}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      ariaHideApp={process.env.NODE_ENV !== 'test'}
    >
      <div data-testid={dataTestId}>
        {!hideCloseButton && (
          <button
            type="button"
            onClick={onRequestClose}
            className="unstyled_btn"
          >
            <i
              className={clsx(classes.close_button, 'fas', 'fa-times')}
              data-testid={`${dataTestId}_close`}
            />
          </button>
        )}
        {title && (
          <>
            <Text
              component="h2"
              variant="h4"
              className={clsx(classes.heading, overrideClasses?.title)}
              data-testid={`${dataTestId}_title`}
            >
              {title}
            </Text>
            <HorizontalRule className={classes.heading_hr} />
          </>
        )}
        <div
          className={clsx(classes.body, overrideClasses?.body)}
          data-testid={`${dataTestId}_body`}
        >
          {children}
        </div>
        {hasFooter && (
          <>
            <HorizontalRule className={classes.body_hr} />
            <div
              className={clsx(classes.footer, overrideClasses?.footer)}
              data-testid={`${dataTestId}_footer`}
            >
              {primaryFooterButton && <Button {...primaryFooterButton} />}
              {secondaryFooterButton && <Button {...secondaryFooterButton} />}
            </div>
          </>
        )}
      </div>
    </ReactModal>
  );
}

export default Modal;
